import { useCallback, useEffect, useRef } from 'react';

// Dedo que anda mais que isso é rolagem (ex.: fileira de chips), não long press.
const MOVE_TOLERANCE_PX = 10;

/**
 * Long press (toque segurado) e clique direito no desktop disparam
 * `onLongPress(element, target)`. Devolve uma função que gera as props do
 * elemento: `{...bind(target)}`.
 *
 * - O clique que vem ao soltar o dedo depois do long press é descartado, para
 *   não acionar também o `onClick` do elemento.
 * - O Android dispara `contextmenu` no long press: a flag `fired` impede abrir
 *   duas vezes (timer + contextmenu).
 */
export default function useLongPress(onLongPress, { delay = 500 } = {}) {
  const timer = useRef(null);
  const origin = useRef(null);
  const fired = useRef(false);

  const clear = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    origin.current = null;
  }, []);

  useEffect(() => clear, [clear]);

  return useCallback(
    (target) => ({
      onPointerDown: (event) => {
        // Todo toque novo zera a flag, inclusive o clique direito (que abre pelo contextmenu).
        fired.current = false;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        origin.current = { x: event.clientX, y: event.clientY };
        const element = event.currentTarget;
        timer.current = setTimeout(() => {
          fired.current = true;
          origin.current = null;
          onLongPress(element, target);
        }, delay);
      },
      onPointerMove: (event) => {
        if (!origin.current) return;
        const moved = Math.hypot(event.clientX - origin.current.x, event.clientY - origin.current.y);
        if (moved > MOVE_TOLERANCE_PX) clear();
      },
      onPointerUp: clear,
      onPointerLeave: clear,
      onPointerCancel: clear,
      onContextMenu: (event) => {
        event.preventDefault();
        clear();
        if (fired.current) return;
        fired.current = true;
        onLongPress(event.currentTarget, target);
      },
      onClickCapture: (event) => {
        if (!fired.current) return;
        fired.current = false;
        event.stopPropagation();
        event.preventDefault();
      },
    }),
    [clear, delay, onLongPress],
  );
}
